import React, { useState, useEffect } from "react";
import { DialogMaximize } from "../components/Form/Dialog";
import toast from "react-hot-toast";
import { useProperties } from "../hooks/useProperty";
import { PropertyFormFields } from "./PropertyForm";
import { EMPTY_FORM_DATA, deriveCwStatusFromMls } from "../constants/propertyFields";

export const EditSoldProperty = ({ isOpen, onClose, title, property }) => {
  const [validationErrors, setValidationErrors] = useState([]);
  const { propertyUpdateLoading, propertyUpdateSuccess, propertyUpdate } = useProperties();

  const [formData, setFormData] = useState({
    ...EMPTY_FORM_DATA,
    listing_type: "Residential Sale",
    state: "FL",
  });

  useEffect(() => {
    if (property && isOpen) {
      const dateFields = [
        "available_date", "date_became_active", "date_became_sold", "date_became_rented",
        "date_became_pending", "date_became_off_market", "date_became_incomplete", "date_became_cancelled", "date_became_withdrawn", "date_became_expired",
        "previous_sold_date",
      ];

      const cleanedProperty = { ...property };
      dateFields.forEach(field => {
        if (cleanedProperty[field]) {
          cleanedProperty[field] = cleanedProperty[field].slice(0, 10);
        }
      });

      setFormData({
        ...EMPTY_FORM_DATA,
        ...cleanedProperty,
      });
    }
  }, [property, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setFormData((prev) => {
        const current = Array.isArray(prev[name]) ? prev[name] : [];
        const updated = checked ? [...current, value] : current.filter((v) => v !== value);
        return { ...prev, [name]: updated };
      });
    } else if (name === "mls_listing_status") {
      setFormData((prev) => {
        const derived =
          deriveCwStatusFromMls(value, prev.listing_type) ??
          prev.cw_listing_status ??
          "ACTIVE";
        let legacyStatus;
        if (derived === "SOLD") legacyStatus = "Sold";
        else if (derived === "RENTED") legacyStatus = "Rented";
        else if (prev.listing_type === "Residential Rental") legacyStatus = "Rent";
        else legacyStatus = "Sale";
        return { ...prev, mls_listing_status: value, cw_listing_status: derived, status: legacyStatus };
      });
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const numericFields = ["year_built", "bedrooms", "bathrooms", "half_bathrooms", "sf_under_ac", "dom"];
  const decimalFields = [
    "price_or_rent", "rented_price", "sold_price",
    "original_asking_price", "original_asking_rent",
    "previous_sold_price",
    "real_estate_tax", "county_assessed_value",
  ];

  const formatFormData = (data) => {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key,
        numericFields.includes(key)
          ? value === "" || value === null ? null : parseInt(value, 10) || null
          : decimalFields.includes(key)
          ? value === "" || value === null ? null : parseFloat(value) || null
          : Array.isArray(value)
          ? JSON.stringify(value)
          : value === "" ? null : value,
      ])
    );
  };

  const validateForm = () => {
    const errors = [];
    if (!formData.street_name) errors.push("Street Name is required");
    if (!formData.city) errors.push("City is required");
    if (!formData.state) errors.push("State is required");
    if (!formData.zip_code) errors.push("Zip Code is required");
    if (formData.zip_code && !/^\d{5}$/.test(formData.zip_code)) errors.push("Zip Code must be exactly 5 digits");
    if (!formData.county_name) errors.push("County is required");
    const lt = formData.listing_type || "";
    const ls = formData.cw_listing_status || "";
    if (lt === "Residential Sale" && ls === "ACTIVE" && !formData.rcr_seller) errors.push("RCR Seller is required");
    if (lt === "Residential Sale" && ls === "SOLD" && !formData.rcr_buyer) errors.push("RCR Buyer is required");
    if (lt === "Residential Rental" && ls === "ACTIVE" && !formData.rcr_landlord) errors.push("RCR Landlord is required");
    if (lt === "Residential Rental" && ls === "RENTED" && !formData.rcr_tenant) errors.push("RCR Tenant is required");
    if (!formData.deviation_from_original_data) errors.push("Deviation From Original Data is required");
    if ((ls === "SOLD" || ls === "RENTED") && !formData.anomaly) errors.push("Anomaly is required when status is SOLD or RENTED");
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (errors.length > 0) {
      setValidationErrors(errors);
      toast.error("Please fix the validation errors");
      return;
    }
    setValidationErrors([]);
    try {
      const formattedData = formatFormData(formData);
      propertyUpdate({ id: property.id, payload: formattedData });
    } catch (error) {
      console.error("Error:", error);
    }
  };

  useEffect(() => {
    if (propertyUpdateSuccess) {
      onClose();
    }
  }, [propertyUpdateSuccess]);

  if (!isOpen) return null;

  return (
    <DialogMaximize
      visible={isOpen}
      header={title}
      onClose={onClose}
      body={
        <form onSubmit={handleSubmit} className="max-w-2xl mx-auto p-6 bg-white relative">
          <div className="sticky top-0 z-10 bg-white pb-4 mb-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <button type="button" onClick={onClose} className="bg-[#50565aa4] text-white px-4 py-2 rounded hover:bg-[#50565a]">Cancel</button>
              <button type="submit" disabled={propertyUpdateLoading} className="bg-red-500 text-white px-6 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg">
                {propertyUpdateLoading ? "Updating..." : "Update"}
              </button>
            </div>
          </div>

          <PropertyFormFields formData={formData} handleChange={handleChange} mode="edit-sold" errors={validationErrors} />

          <div className="flex my-4 justify-between">
            <button type="button" onClick={onClose} className="bg-[#50565aa4] text-white px-4 py-2 rounded">Cancel</button>
            <button type="submit" disabled={propertyUpdateLoading} className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50">
              {propertyUpdateLoading ? "Updating..." : "Update"}
            </button>
          </div>
        </form>
      }
    />
  );
};

export default EditSoldProperty;
